import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Input, Select, SelectOptionType, Button, Tooltip } from '@aidkitorg/component-library';
import { useLocalizedStrings } from '../../../Localization';
import { useUserContext } from '../../../Contexts/UserContext';
import { useOrganizationContext } from '../../../Contexts/OrganizationContext';
import { type User } from '@aidkitorg/aidkit/lib/users/UserService';
import { emailValidator } from '../../../utils/emailValidator';

type UserModalProps = {
  isOpen: boolean;
  onClose: () => void;
  user?: User | null;
  mode: 'create' | 'edit';
};

export const UserModal: React.FC<UserModalProps> = ({ isOpen, onClose, user, mode }) => {
  const L = useLocalizedStrings();
  const { createUser, updateUser, userGroups, loadUserGroups, loadUsers } = useUserContext();
  const { selectedOrganization, organizations, canSelectMultiple } = useOrganizationContext();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    user_group: 0,
    organization: selectedOrganization?.uid || null,
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when modal opens/closes or user changes
  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && user) {
        setFormData({
          name: user.name || '',
          email: user.email || '',
          phone: user.phone || '',
          user_group: user.user_group || 0,
          organization: user.organization || selectedOrganization?.uid || null,
        });
      } else {
        setFormData({
          name: '',
          email: '',
          phone: '',
          user_group: 0,
          organization: selectedOrganization?.uid || null,
        });
      }
    }
  }, [isOpen, mode, user, selectedOrganization]);

  // Load user groups when modal opens or organization changes
  useEffect(() => {
    if (isOpen) {
      // Pass the form's organization directly (null if "No Organization" is selected)
      loadUserGroups(formData.organization);
    }
  }, [isOpen, loadUserGroups, formData.organization]);

  const userGroupOptions: SelectOptionType[] = userGroups.map(group => ({
    label: group.name,
    value: group.id.toString(),
  }));

  // Organization field logic
  const shouldShowOrganizationField = organizations.length > 0;
  const shouldDisableOrganizationField = selectedOrganization !== undefined || organizations.length === 1;
  
  const organizationOptions: SelectOptionType[] = [
    { label: L.admin_panel.user_management.no_organization, value: '' },
    ...organizations.map(org => ({
      label: org.name,
      value: org.uid,
    }))
  ];

  const getSelectedOrganizationOption = () => {
    if (formData.organization) {
      return organizationOptions.find(opt => opt.value === formData.organization) || undefined;
    }
    // Return the "No Organization" option when organization is null/empty
    return organizationOptions.find(opt => opt.value === '') || undefined;
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleUserGroupChange = (option: SelectOptionType | null) => {
    if (option) {
      setFormData(prev => ({
        ...prev,
        user_group: parseInt(option.value)
      }));
    }
  };

  const handleOrganizationChange = (option: SelectOptionType | null) => {
    setFormData(prev => ({
      ...prev,
      organization: (option?.value && option.value !== '') ? option.value : null,
      user_group: 0 // Reset user group when organization changes
    }));
  };

  const isFormValid = () => {
    return formData.name.trim() !== '' &&
           formData.email.trim() !== '' &&
           emailValidator(formData.email) &&
           formData.user_group > 0; // Changed from !== 0 to > 0 for clarity
  };

  const handleSubmit = useCallback(async () => {
    if (!isFormValid()) return;

    setIsSubmitting(true);
    try {
      let success = false;
      
      if (mode === 'create') {
        success = await createUser(formData)
      } else if (mode === 'edit' && user?.uid) {
        success = await updateUser(user.uid, formData);
      }

      if (success) {
        // Reload users to get the properly formatted data with organization and user group names
        const organization = selectedOrganization === null ? null : selectedOrganization?.uid || undefined;
        loadUsers({
          organization,
          filter: undefined,
        });
        onClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [mode, formData, createUser, updateUser, user?.uid, onClose, loadUsers, selectedOrganization]);

  const modalTitle = mode === 'create' ? L.admin_panel.user_management.create_user : L.admin_panel.user_management.edit_user;

  // Determine if user can be edited
  const canEdit = mode === 'create' || (user?.can_edit !== false);
  const isReadOnly = mode === 'edit' && !canEdit;

  // Determine tooltip text for disabled save button
  const getDisabledTooltip = () => {
    if (isReadOnly) {
      return user?.source !== 'User Management'
        ? L.user.edit_user_disabled
        : L.user.edit_user_admin_only;
    }
    return undefined;
  };

  return (
    <Modal open={isOpen} onClose={() => onClose()} title={modalTitle} size="lg">
      {/* Form Body */}
      <div className="py-4">
        <div className="space-y-4 [&>*]:!max-w-none [&>*]:w-full">
          <Input
            label={L.admin_panel.user_management.name}
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            required
            disabled={isReadOnly}
          />

          <Input
            label={L.admin_panel.user_management.email}
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            required
            disabled={isReadOnly}
            error={{
              hasError: formData.email.trim() !== '' && !emailValidator(formData.email),
              errorMessage: L.questions.textentry.must_be_a_valid_email
            }}
          />

          <Input
            label={L.admin_panel.user_management.phone}
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            disabled={isReadOnly}
          />

          {shouldShowOrganizationField && (
            <Select
              label={L.admin_panel.user_management.organization}
              labelLocation="above"
              options={organizationOptions}
              value={getSelectedOrganizationOption()}
              onChange={handleOrganizationChange}
              disabled={shouldDisableOrganizationField || isReadOnly}
              description=""
              className="w-full"
            />
          )}

          <Select
            label={L.admin_panel.user_management.user_group}
            labelLocation="above"
            options={userGroupOptions}
            value={userGroupOptions.find(option => option.value === formData.user_group.toString()) || undefined}
            onChange={handleUserGroupChange}
            description={L.admin_panel.user_management.select_user_group_description}
            placeholder={L.admin_panel.user_management.select_user_group_placeholder}
            className="w-full"
            required
            disabled={isReadOnly}
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="py-4">
        <div className="flex justify-end space-x-3">
          <Button variant="secondary" onClick={onClose}>
            {isReadOnly ? L.apply.close : L.apply.cancel}
          </Button>
          {isReadOnly ? (
            <Tooltip text={getDisabledTooltip()} asChild={true}>
              <Button
                isDisabled={true}
              >
                {L.admin_panel.user_management.save}
              </Button>
            </Tooltip>
          ) : (
            <Button
              onClick={handleSubmit}
              isDisabled={!isFormValid() || isSubmitting}
              loading={isSubmitting}
            >
              {mode === 'create' ? L.admin_panel.user_management.create_user : L.admin_panel.user_management.save}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};